// src/app/interceptors/auth.interceptor.ts
import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError, retry, timer, Observable } from 'rxjs';
import { TokenService } from '../services/token.service';
import { AuthErrorService } from '../services/auth-error.service';
import { getAuth } from 'firebase/auth';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { LoggerService } from '../services/logger.service';

// MEJORADO: Cache para evitar múltiples refreshes simultáneos de token
let tokenRefreshPromise: Promise<string> | null = null;

// MEJORADO: Contador de errores 401 consecutivos para detectar problemas temporales
interface Error401Log {
  timestamp: number;
  url: string;
}

function getRecent401Errors(): Error401Log[] {
  try {
    const log = sessionStorage.getItem('recent_401_errors');
    if (!log) return [];
    const errors: Error401Log[] = JSON.parse(log);
    const now = Date.now();
    // Mantener solo errores de los últimos 30 segundos
    return errors.filter(e => (now - e.timestamp) < 30000);
  } catch {
    return [];
  }
}

function add401Error(url: string): void {
  try {
    const errors = getRecent401Errors();
    errors.push({ timestamp: Date.now(), url });
    // Mantener solo los últimos 10 errores
    if (errors.length > 10) errors.shift();
    sessionStorage.setItem('recent_401_errors', JSON.stringify(errors));
  } catch {
    // Ignorar errores de storage
  }
}

function clear401Errors(): void {
  try {
    sessionStorage.removeItem('recent_401_errors');
  } catch {
    // Ignorar errores de storage
  }
}

/**
 * Interceptor HTTP funcional que agrega automáticamente los tokens correctos
 * a todas las peticiones según el tipo de API (nueva o vieja)
 * 
 * ✅ COMPATIBILIDAD:
 * - APIs nuevas (/api/*): Usa Firebase ID Token en Authorization header
 * - APIs viejas (sin /api/): Usa authCoreToken en authCoreKey header
 * 
 * El authCoreToken generado por el nuevo login es 100% compatible con APIs viejas
 */
export const authInterceptorFn: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(TokenService);
  const router = inject(Router);
  const authErrorService = inject(AuthErrorService);
  // Inject once at interceptor creation time to avoid context loss inside async error handlers
  const httpClient = inject(HttpClient);
  const logger = inject(LoggerService);
  
  // Determinar si es API nueva o vieja
  const isNewApi = req.url.includes('/api/web/') || 
                   req.url.includes('/api/mobile/') || 
                   req.url.includes('/api/core/');
  
  // Excluir endpoints que no necesitan autenticación
  const isLoginEndpoint = req.url.includes('/autenticacion/login') || 
                          req.url.includes('/auth/login');
  
  // Endpoint de logout necesita authCoreKey además de Authorization
  const isLogoutEndpoint = req.url.includes('/autenticacion/logout');
  
  if (isLoginEndpoint) {
    // No agregar headers a endpoints de login
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error);
      })
    );
  }
  
  if (isNewApi) {
    // APIs nuevas: usar Firebase ID Token
    return from(tokenService.getFirebaseIdToken()).pipe(
      switchMap(idToken => {
        // ✅ No reemplazar headers existentes, solo agregar los que faltan
        let clonedReq = req;
        
        // Agregar Content-Type solo si no existe Y no es FormData
        // (FormData necesita que el navegador establezca automáticamente el boundary)
        if (!req.headers.has('Content-Type') && !(req.body instanceof FormData)) {
          clonedReq = clonedReq.clone({
            setHeaders: { 'Content-Type': 'application/json' }
          });
        }
        
        // Agregar x-client-day solo si no existe
        if (!clonedReq.headers.has('x-client-day')) {
          clonedReq = clonedReq.clone({
            setHeaders: { 'x-client-day': getClientDay() }
          });
        }
        
        // Agregar Authorization si tenemos idToken
        if (idToken && !clonedReq.headers.has('Authorization')) {
          clonedReq = clonedReq.clone({
            setHeaders: { 'Authorization': `Bearer ${idToken}` }
          });
        }
        
        // ⚠️ CASO ESPECIAL: El endpoint de logout también necesita authCoreKey
        if (isLogoutEndpoint && !clonedReq.headers.has('authCoreKey')) {
          const backendToken = tokenService.getBackendToken();
          if (backendToken) {
            clonedReq = clonedReq.clone({
              setHeaders: { 'authCoreKey': backendToken }
            });
          }
        }
        
        return next(clonedReq).pipe(
          catchError((error: HttpErrorResponse) => {
            // Intentar reintentar con token refrescado antes de manejar el error
            return from(retryRequestWithRefreshedToken(
              clonedReq,
              error,
              tokenService,
              next,
              true, // isNewApi = true
              httpClient, // Pasar HttpClient para sessionLogin
              logger
            )).pipe(
              switchMap((retryResult) => {
                // Si el reintento fue exitoso, retornar el Observable de la petición reintentada
                if (retryResult) {
                  return retryResult;
                }

                // Si no se pudo reintentar, manejar el error normalmente
                return from(handleError(error, tokenService, router, authErrorService, true, logger)).pipe(
                  switchMap(result => result)
                );
              })
            );
          })
        );
      })
    );
  } else {
    // APIs viejas: usar Backend Token (authCoreToken del nuevo login)
    // ✅ No reemplazar headers existentes, solo agregar los que faltan
    let clonedReq = req;
    
    // Agregar Content-Type solo si no existe Y no es FormData
    // (FormData necesita que el navegador establezca automáticamente el boundary)
    if (!req.headers.has('Content-Type') && !(req.body instanceof FormData)) {
      clonedReq = clonedReq.clone({
        setHeaders: { 'Content-Type': 'application/json' }
      });
    }
    
    // Agregar x-client-day solo si no existe
    if (!clonedReq.headers.has('x-client-day')) {
      clonedReq = clonedReq.clone({
        setHeaders: { 'x-client-day': getClientDay() }
      });
    }
    
    // Agregar authCoreKey solo si no existe y tenemos token
    if (!clonedReq.headers.has('authCoreKey')) {
      const backendToken = tokenService.getBackendToken();
      if (backendToken) {
        clonedReq = clonedReq.clone({
          setHeaders: { 'authCoreKey': backendToken }
        });
      } else {
      }
    }
    
    return next(clonedReq).pipe(
      catchError((error: HttpErrorResponse) => {
        // Para APIs viejas, también intentar reintentar (aunque generalmente no es necesario)
        // ya que el backend token es más estable
        return from(retryRequestWithRefreshedToken(
          clonedReq,
          error,
          tokenService,
          next,
          false, // isNewApi = false
          httpClient, // Pasar HttpClient aunque no se use para APIs viejas
          logger
        )).pipe(
          switchMap((retryResult) => {
            // Si el reintento fue exitoso, retornar el Observable de la petición reintentada
            if (retryResult) {
              return retryResult;
            }

            // Si no se pudo reintentar, manejar el error normalmente
            return from(handleError(error, tokenService, router, authErrorService, false, logger)).pipe(
              switchMap(result => result)
            );
          })
        );
      })
    );
  }
};

/**
 * Intenta refrescar el token y reintentar la petición original
 * Retorna Observable de la petición reintentada si el refresh fue exitoso
 * Retorna null si no se puede reintentar (no es 401, no hay sesión válida, etc.)
 */
async function retryRequestWithRefreshedToken(
  req: HttpRequest<any>,
  error: HttpErrorResponse,
  tokenService: TokenService,
  next: HttpHandlerFn,
  isNewApi: boolean,
  httpClient?: HttpClient,
  logger?: LoggerService
): Promise<Observable<HttpEvent<any>> | null> {
  // Solo reintentar si es error 401
  if (error.status !== 401) {
    return null;
  }

  // Verificar si hay sesión válida
  const auth = getAuth();
  const user = auth.currentUser;
  const userData = localStorage.getItem('data_user');
  const hasBackendToken = !!tokenService.getBackendToken();

  // Si no hay sesión válida, no reintentar
  if (!user || !userData || !hasBackendToken) {
    logger?.log('⚠️ [AuthInterceptor] No hay sesión válida completa, no reintentando');
    return null;
  }

  // Verificar si es error de conexión (no reintentar)
  const isConnectionError = 
    error.message?.includes('NetworkError') ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('timeout') ||
    error.message?.includes('network') ||
    (error as any).name === 'NetworkError' ||
    (!error.error && error.status === 401);

  if (isConnectionError) {
    logger?.log('⚠️ [AuthInterceptor] Error de conexión detectado, no reintentando');
    return null;
  }

  // Verificar si hay mensaje del backend (si no hay, probablemente es error de conexión)
  const hasBackendMessage = 
    error?.error?.msg || 
    error?.error?.message || 
    (typeof error?.error === 'string' && error.error.trim().length > 0);

  if (!hasBackendMessage) {
    logger?.log('⚠️ [AuthInterceptor] No hay mensaje del backend, probablemente error de conexión, no reintentando');
    return null;
  }

  try {
    logger?.log('🔄 [AuthInterceptor] Intentando refrescar token para reintentar petición...');

    if (isNewApi) {
      // APIs nuevas: refrescar Firebase ID Token
      // MEJORADO: Usar cache para evitar múltiples refreshes simultáneos
      let newToken: string;
      
      if (tokenRefreshPromise) {
        // Ya hay un refresh en progreso, esperar a que termine
        logger?.log('⏳ [AuthInterceptor] Ya hay un refresh en progreso, esperando...');
        try {
          newToken = await tokenRefreshPromise;
        } catch (e) {
          // Si el refresh en cache falló, intentar uno nuevo
          tokenRefreshPromise = null;
          throw e;
        }
      } else {
        // Crear nuevo refresh
        const refreshPromise = user.getIdToken(true);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token refresh timeout')), 15000)
        );
        
        tokenRefreshPromise = Promise.race([refreshPromise, timeoutPromise]) as Promise<string>;
        
        try {
          newToken = await tokenRefreshPromise;
        } finally {
          // Limpiar cache después de 1 segundo (dar tiempo a otras peticiones que puedan estar esperando)
          setTimeout(() => {
            tokenRefreshPromise = null;
          }, 1000);
        }
      }
      
      if (newToken) {
        // NUEVO: Si el error 401 viene de una API nueva y tenemos Firebase ID Token válido,
        // pero el backend no encuentra sesión, intentar recrear la sesión con sessionLogin
        const errorMessage = error?.error?.msg || error?.error?.message || (typeof error?.error === 'string' ? error.error : '');
        const sessionNotFound = 
          errorMessage.toLowerCase().includes('no se encontró sesión') ||
          errorMessage.toLowerCase().includes('sin sesión') ||
          errorMessage.toLowerCase().includes('session not found') ||
          errorMessage.toLowerCase().includes('sin sesión en bd');
        
        if (sessionNotFound && httpClient) {
          logger?.log('🔄 [AuthInterceptor] Sesión no encontrada en BD, intentando recrear con sessionLogin...');
          
          try {
            const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            
            const sessionResponse = await httpClient.post(
              `${environment.url_api}/api/web/autenticacion/sessionLogin`,
              { 
                idToken: newToken, 
                timeZone: timeZone 
              }
            ).toPromise() as any;
            
            if (sessionResponse?.authCoreToken) {
              // Actualizar el authCoreToken en el cliente
              tokenService.updateBackendToken(sessionResponse.authCoreToken);
              
              logger?.log('✅ [AuthInterceptor] Sesión recreada exitosamente, authCoreToken actualizado');
            }
          } catch (sessionError: any) {
            logger?.error('❌ [AuthInterceptor] Error al recrear sesión con sessionLogin:', sessionError);
            // Continuar con el reintento normal, puede que funcione
          }
        }
        
        logger?.log('✅ [AuthInterceptor] Token refrescado, esperando 500ms antes de reintentar (para equipos lentos)...');
        
        // MEJORADO: Agregar pequeño delay antes de reintentar para dar tiempo a equipos lentos
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // MEJORADO: Limpiar contador de errores 401 si el refresh fue exitoso
        clear401Errors();
        
        logger?.log('🔄 [AuthInterceptor] Reintentando petición con nuevo token...');
        
        // Clonar la petición original con el nuevo token
        const retryReq = req.clone({
          setHeaders: {
            'Authorization': `Bearer ${newToken}`
          }
        });

        // Reintentar la petición
        return next(retryReq);
      }
    } else {
      // APIs viejas: intentar recuperar la sesión con sessionLogin y reintentar
      logger?.log('🔄 [AuthInterceptor] API vieja 401 — intentando recovery con sessionLogin...');

      // Obtener Firebase ID Token fresco para llamar sessionLogin
      let freshIdToken: string;

      if (tokenRefreshPromise) {
        try {
          freshIdToken = await tokenRefreshPromise;
        } catch (e) {
          tokenRefreshPromise = null;
          throw e;
        }
      } else {
        const refreshPromise = user.getIdToken(true);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Token refresh timeout')), 15000)
        );

        tokenRefreshPromise = Promise.race([refreshPromise, timeoutPromise]) as Promise<string>;

        try {
          freshIdToken = await tokenRefreshPromise;
        } finally {
          setTimeout(() => { tokenRefreshPromise = null; }, 1000);
        }
      }

      if (freshIdToken && httpClient) {
        try {
          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const sessionResponse = await httpClient.post(
            `${environment.url_api}/api/web/autenticacion/sessionLogin`,
            { idToken: freshIdToken, timeZone }
          ).toPromise() as any;

          if (sessionResponse?.authCoreToken) {
            tokenService.updateBackendToken(sessionResponse.authCoreToken);
            logger?.log('✅ [AuthInterceptor] Sesión recreada para API vieja, authCoreToken actualizado');
          }
        } catch (sessionError: any) {
          logger?.error('❌ [AuthInterceptor] Error al recrear sesión para API vieja:', sessionError);
          // Si sessionLogin falla, no reintentar — caerá en handleError (ultra-conservador)
          return null;
        }

        await new Promise(resolve => setTimeout(resolve, 500));
        clear401Errors();

        // Reintentar la petición original con el nuevo authCoreKey
        const newBackendToken = tokenService.getBackendToken();
        const retryReq = req.clone({
          setHeaders: {
            'authCoreKey': newBackendToken || ''
          }
        });

        logger?.log('🔄 [AuthInterceptor] Reintentando petición de API vieja con nuevo authCoreKey...');
        return next(retryReq);
      }

      return null;
    }
  } catch (refreshError: any) {
    logger?.error('❌ [AuthInterceptor] Error al refrescar token para reintento:', refreshError);
    
    // MEJORADO: Si es timeout, no cerrar sesión (puede ser equipo lento)
    if (refreshError.message?.includes('timeout') || refreshError.message === 'Token refresh timeout') {
      logger?.warn('⚠️ [AuthInterceptor] Timeout al refrescar token (posible equipo lento), no reintentando pero NO cerrando sesión');
      return null;
    }
    
    // Si el error es de token expirado, no reintentar
    const isTokenExpired = 
      refreshError.code === 'auth/user-token-expired' || 
      refreshError.code === 'auth/invalid-user-token' ||
      refreshError.code === 'auth/user-disabled' ||
      refreshError.message?.includes('expired') ||
      refreshError.message?.includes('invalid');

    if (isTokenExpired) {
      logger?.error('❌ [AuthInterceptor] Token realmente expirado, no reintentando');
      return null;
    }

    // Error de red u otro error temporal, no reintentar
    logger?.warn('⚠️ [AuthInterceptor] Error temporal al refrescar, no reintentando');
    return null;
  }

  return null;
}

/**
 * Maneja errores HTTP, especialmente 401 (no autorizado)
 * ULTRA CONSERVADOR: Solo cierra sesión cuando es 100% necesario
 * - Detecta errores de conexión para evitar falsos positivos
 * - Requiere múltiples verificaciones antes de cerrar sesión
 * - NOTA: El refresh de token y reintento se maneja en retryRequestWithRefreshedToken
 */
async function handleError(
  error: HttpErrorResponse,
  tokenService: TokenService,
  router: Router,
  authErrorService: AuthErrorService,
  skipRefresh: boolean = false,
  logger?: LoggerService
): Promise<ReturnType<typeof throwError>> {
  // ✅ POLÍTICA ULTRA CONSERVADORA: 
  // Solo cerrar sesión si TODAS estas condiciones se cumplen:
  // 1. Es un error 401 (no otros códigos)
  // 2. NO es error de conexión (status 0, timeout, etc.)
  // 3. Hay mensaje del backend (no es error de red)
  // 4. El token realmente está expirado (no se pudo refrescar)
  // 5. NO hay usuario en Firebase O NO hay data_user
  // 6. Se han verificado múltiples veces que no hay sesión válida
  
  // ✅ DETECTAR ERRORES DE CONEXIÓN PRIMERO
  // Status 0 = error de conexión (sin respuesta del servidor)
  // Timeout, network error, CORS, etc.
  if (error.status === 0 || error.status === null || error.status === undefined) {
    logger?.warn('⚠️ [AuthInterceptor] Error de conexión detectado (status 0)');
    
    // Verificar si realmente es un error de conexión
    const isNetworkError = 
      error.message?.includes('NetworkError') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('network') ||
      error.message?.includes('timeout') ||
      (error as any).name === 'NetworkError' ||
      !navigator.onLine;
    
    if (isNetworkError || !navigator.onLine) {
      logger?.warn('⚠️ [AuthInterceptor] Error de red confirmado, no cerrando sesión');
      // No cerrar sesión por errores de conexión
      // El componente puede manejar este error según su lógica
      return throwError(() => error);
    }
    
    // Si no es claramente un error de red, podría ser CORS u otro problema
    // No cerrar sesión automáticamente
    logger?.warn('⚠️ [AuthInterceptor] Error sin status (posible CORS o timeout), no cerrando sesión');
    return throwError(() => error);
  }
  
  if (error.status === 401) {
    // MEJORADO: Ignorar errores 401 de notificaciones (no críticos, no deben cerrar sesión)
    const isNotificationRequest = error.url?.includes('/notificaciones') || 
                                  error.url?.includes('/notificaciones/contador');
    
    if (isNotificationRequest) {
      logger?.warn('⚠️ [AuthInterceptor] Error 401 en notificaciones (no crítico, ignorando):', error.url);
      // No cerrar sesión por errores de notificaciones, solo retornar el error
      return throwError(() => error);
    }
    
    // MEJORADO: Registrar error 401 para contador de errores consecutivos
    add401Error(error.url || 'unknown');
    const recent401Errors = getRecent401Errors();
    
    // Registrar el error para debugging
    const errorInfo = {
      url: error.url || 'unknown',
      timestamp: new Date().toISOString(),
      message: error.message || 'Unknown error',
      status: error.status,
      hasErrorBody: !!error.error,
      hasBackendMessage: !!(error?.error?.msg || error?.error?.message || (typeof error?.error === 'string' && error.error.trim().length > 0)),
      recent401Count: recent401Errors.length
    };
    
    logger?.warn('⚠️ [AuthInterceptor] Error 401 detectado:', errorInfo);
    
    // MEJORADO: Si hay múltiples errores 401 recientes (3+ en 30 segundos), podría ser problema temporal
    // No cerrar sesión inmediatamente, dar más tiempo
    if (recent401Errors.length >= 3) {
      logger?.warn(`⚠️ [AuthInterceptor] ${recent401Errors.length} errores 401 en los últimos 30 segundos, posible problema temporal. NO cerrando sesión todavía.`);
      // No cerrar sesión todavía, podría ser un problema temporal del servidor
      return throwError(() => error);
    }
    
    // Guardar información del error en sessionStorage para debugging
    try {
      const errorLog = JSON.parse(sessionStorage.getItem('auth_errors') || '[]');
      errorLog.push(errorInfo);
      // Mantener solo los últimos 10 errores
      if (errorLog.length > 10) errorLog.shift();
      sessionStorage.setItem('auth_errors', JSON.stringify(errorLog));
    } catch (e) {
      logger?.warn('No se pudo guardar log de error:', e);
    }
    
    // ✅ VERIFICACIÓN 1: ¿Es realmente un error de autenticación o es error de conexión?
    // MEJORADO: Más indicadores de error de conexión
    const isConnectionError = 
      error.message?.includes('NetworkError') ||
      error.message?.includes('Failed to fetch') ||
      error.message?.includes('timeout') ||
      error.message?.includes('network') ||
      error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
      error.message?.includes('ERR_NETWORK_CHANGED') ||
      error.message?.includes('ERR_CONNECTION_REFUSED') ||
      error.message?.includes('ERR_CONNECTION_RESET') ||
      (error as any).name === 'NetworkError' ||
      (error as any).name === 'TimeoutError' ||
      !navigator.onLine ||
      (!error.error && error.status === 401); // 401 sin body puede ser error de conexión
    
    if (isConnectionError) {
      logger?.warn('⚠️ [AuthInterceptor] Error 401 parece ser error de conexión, NO cerrando sesión');
      return throwError(() => error);
    }
    
    // ✅ VERIFICACIÓN 2: ¿Hay mensaje del backend?
    // Si no hay mensaje del backend, probablemente es error de conexión
    const hasBackendMessage = 
      error?.error?.msg || 
      error?.error?.message || 
      (typeof error?.error === 'string' && error.error.trim().length > 0);
    
    if (!hasBackendMessage) {
      logger?.warn('⚠️ [AuthInterceptor] Error 401 sin mensaje del backend, probablemente error de conexión, NO cerrando sesión');
      return throwError(() => error);
    }
    
    // ✅ VERIFICACIÓN 3: Verificar si hay sesión válida
    // NOTA: Si skipRefresh es true, ya se intentó refrescar en retryRequestWithRefreshedToken
    const auth = getAuth();
    const user = auth.currentUser;
    const userData = localStorage.getItem('data_user');
    const backendToken = tokenService.getBackendToken();
    const hasBackendToken = !!backendToken;
    
    // Si ya se intentó refrescar (skipRefresh = true), no intentar de nuevo
    if (skipRefresh) {
      logger?.log('ℹ️ [AuthInterceptor] Ya se intentó refrescar el token anteriormente, saltando refresh en handleError');
    } else if (user && userData && hasBackendToken) {
      // Si no se ha intentado refrescar y hay sesión válida, intentar refrescar como último recurso
      logger?.log('✅ [AuthInterceptor] Sesión válida detectada, intentando refrescar como último recurso...');
      
      try {
        // MEJORADO: Agregar timeout más largo para equipos lentos (15 segundos)
        const refreshPromise = user.getIdToken(true);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Token refresh timeout')), 15000)
        );
        
        const newToken = await Promise.race([refreshPromise, timeoutPromise]) as string;
        
        if (newToken) {
          logger?.log('✅ [AuthInterceptor] Token refrescado exitosamente en handleError, NO cerrando sesión');
          return throwError(() => error);
        }
      } catch (refreshError: any) {
        logger?.error('❌ [AuthInterceptor] Error al refrescar token en handleError:', refreshError);
        
        // MEJORADO: Si es timeout, no cerrar sesión (puede ser equipo lento)
        if (refreshError.message?.includes('timeout') || refreshError.message === 'Token refresh timeout') {
          logger?.warn('⚠️ [AuthInterceptor] Timeout al refrescar token (posible equipo lento), NO cerrando sesión');
          return throwError(() => error);
        }
        
        const isTokenExpired = 
          refreshError.code === 'auth/user-token-expired' || 
          refreshError.code === 'auth/invalid-user-token' ||
          refreshError.code === 'auth/user-disabled' ||
          refreshError.message?.includes('expired') ||
          refreshError.message?.includes('invalid');
        
        if (!isTokenExpired) {
          logger?.warn('⚠️ [AuthInterceptor] Error temporal al refrescar, NO cerrando sesión');
          return throwError(() => error);
        }
        
        logger?.error('❌ [AuthInterceptor] Token realmente expirado después de intentar refrescar');
      }
    }
    
    // ✅ VERIFICACIÓN 4: Verificar múltiples veces que no hay sesión válida
    // Si hay CUALQUIERA de estos elementos, NO cerrar sesión todavía
    if (user || userData || hasBackendToken) {
      logger?.warn('⚠️ [AuthInterceptor] Hay elementos de sesión válida, NO cerrando sesión automáticamente');
      logger?.warn('   - Usuario Firebase:', !!user);
      logger?.warn('   - data_user:', !!userData);
      logger?.warn('   - Backend token:', hasBackendToken);
      return throwError(() => error);
    }
    
    // ✅ VERIFICACIÓN 5: Verificación final antes de cerrar sesión
    // Solo cerrar sesión si TODAS estas condiciones se cumplen:
    // 1. NO hay usuario en Firebase
    // 2. NO hay data_user en localStorage
    // 3. NO hay backend token
    // 4. El error tiene mensaje del backend (confirmado arriba)
    // 5. El token realmente está expirado (si se intentó refrescar)
    
    const finalCheck = {
      hasUser: !!user,
      hasUserData: !!userData,
      hasBackendToken: hasBackendToken,
      hasBackendMessage: hasBackendMessage
    };
    
    logger?.log('🔍 [AuthInterceptor] Verificación final antes de cerrar sesión:', finalCheck);
    
    // Si hay CUALQUIER indicio de sesión válida, NO cerrar sesión
    if (finalCheck.hasUser || finalCheck.hasUserData || finalCheck.hasBackendToken) {
      logger?.warn('⚠️ [AuthInterceptor] Hay indicios de sesión válida, NO cerrando sesión');
      return throwError(() => error);
    }
    
    // ✅ ÚLTIMA VERIFICACIÓN: Verificar que el mensaje del backend realmente indica expiración
    const errorMessage = error?.error?.msg || error?.error?.message || (typeof error?.error === 'string' ? error.error : '');
    const indicatesExpiration = 
      errorMessage.toLowerCase().includes('expirad') ||
      errorMessage.toLowerCase().includes('expired') ||
      errorMessage.toLowerCase().includes('token inválido') ||
      errorMessage.toLowerCase().includes('invalid token') ||
      errorMessage.toLowerCase().includes('no autorizado') ||
      errorMessage.toLowerCase().includes('unauthorized');
    
    if (!indicatesExpiration && errorMessage.trim().length > 0) {
      // El mensaje del backend no indica expiración, podría ser otro tipo de error
      logger?.warn('⚠️ [AuthInterceptor] Mensaje del backend no indica expiración, NO cerrando sesión');
      return throwError(() => error);
    }
    
    // ✅ TODAS LAS VERIFICACIONES PASARON: Cerrar sesión
    logger?.error('❌ [AuthInterceptor] TODAS las verificaciones indican que la sesión es inválida, cerrando sesión');
    
    // MEJORADO: Limpiar contador de errores 401 antes de cerrar sesión
    clear401Errors();
    
    // Extraer mensaje del error del backend (hasBackendMessage ya está definido arriba)
    let mensajeError = 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
    
    if (hasBackendMessage) {
      if (error?.error?.msg) {
        mensajeError = error.error.msg;
      } else if (error?.error?.message) {
        mensajeError = error.error.message;
      } else if (typeof error?.error === 'string') {
        mensajeError = error.error;
      }
      
      // Solo mostrar modal si hay mensaje del backend (es un error real de autenticación)
      authErrorService.showSessionExpired(mensajeError);
    } else {
      // No hay mensaje del backend, probablemente es error de conexión
      // No mostrar modal de sesión expirada, dejar que el componente maneje el error
      logger?.warn('⚠️ [AuthInterceptor] Error 401 sin mensaje del backend, no mostrando modal de sesión expirada');
    }
    
    tokenService.clearAllTokens();
    localStorage.removeItem('data_user');
    localStorage.removeItem('last_auth_validation');
    localStorage.removeItem('session_login_day');
    localStorage.removeItem('login_in_progress');
    
    // Guardar causa del logout
    sessionStorage.setItem('logout_reason', JSON.stringify({
      reason: '401_token_expired',
      timestamp: new Date().toISOString(),
      url: error.url || router.url
    }));
    
    // Redirigir a login (solo si no estamos ya en login)
    if (!router.url.includes('/login')) {
      router.navigate(['/login']);
    }
  }
  
  return throwError(() => error);
}

/**
 * Obtiene el día actual en formato YYYY-MM-DD
 */
function getClientDay(): string {
  return new Date().toISOString().split('T')[0];
}

